import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import type { LinkProps } from '@mui/material/Link';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { createLink } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { forwardRef, type FC, type ReactNode } from 'react';

import { hasHTML } from '#/utils/has-html';

const FONT_SIZE = { xs: 14, md: 16 };

/**
 * Breadcrumb trail shared by the Program / Lesson pages (space home, module,
 * lesson detail and the content viewers). Rendered inside `StickyNavRow`.
 */
export const NavBreadcrumbs: FC<{ children: ReactNode }> = ({ children }) => (
  <Breadcrumbs
    sx={{
      fontFamily: '"Figtree", sans-serif',
      fontSize: FONT_SIZE,
      lineHeight: { xs: '20px', md: '22px' },
      fontWeight: 400,
      color: 'grey.600',
      '& .MuiBreadcrumbs-separator': { mx: 0.6 },
    }}
  >
    {children}
  </Breadcrumbs>
);

const GreyLink = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <MuiLink ref={ref} underline="none" sx={{ color: 'grey.600' }} {...props} />
));
GreyLink.displayName = 'GreyLink';

/**
 * A clickable ancestor crumb in the trail's grey. Built with `createLink` (the
 * same way as `TanstackMUILink`) so `to` / `params` / `search` stay route-typed.
 */
export const NavBreadcrumbLink = createLink(GreyLink);

/**
 * Admin-authored name that may contain rich HTML (space / module names).
 * Sanitised before rendering; rich markup gets nudged up so it baselines
 * with the plain-text crumbs around it.
 */
export const NavBreadcrumbLabel: FC<{ html: string }> = ({ html }) => (
  <Typography variant="body2" component="span" sx={{ mt: hasHTML(html) ? -0.5 : 0, fontSize: FONT_SIZE }}>
    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
  </Typography>
);

/** The last, bold crumb — the page the user is on. Extra children (e.g. a DraftBadge) sit inline after it. */
export const NavBreadcrumbCurrent: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
    {children}
  </Box>
);
